import { faEllipsisV } from '@fortawesome/pro-light-svg-icons/faEllipsisV'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Popover from '@qc/particle/lib/components/popover'
import Button from 'antd/es/button'
import React, { MouseEvent, MutableRefObject, useCallback, useState } from 'react'
import VisibilitySensor from 'react-visibility-sensor'
import SimpleMarkdown from 'simple-markdown'
import { Notification, NotificationStatus, NotificationViewed } from '../../../models'
import { lastAccessDateFormat, renderDateDiff } from '../../../utils/date'

import styles from './NotificationBlock.scss'

const NotificationBlock = ({
  notification,
  onVisibilityChange,
  containment ,
  onArchive
}: {
  notification: Notification,
  onVisibilityChange: (notif: Notification) => void,
  containment: MutableRefObject<HTMLDivElement>,
  onArchive: (notification: Notification) => void
}) => {
  const [actionOpen, setActionOpen] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const [timer, setTimer] = useState(null)

  const blockClassNames = [ styles.notification_block ]
  if (notification.viewed === NotificationViewed.UNREAD) blockClassNames.push(styles.unread)
  if (dismissed) blockClassNames.push(styles.dismissed)

  const onArchiveClicked = (event: MouseEvent) => {
    setDismissed(true)
    setActionOpen(false)
  }

  const onTransitionEnd = useCallback((event: React.TransitionEvent, notif: Notification) => {
    if (dismissed && event.nativeEvent.propertyName === 'transform' && event.nativeEvent.elapsedTime > 0.2) {
      onArchive(notif)
    }
  }, [dismissed])

  const setVisible = useCallback((isVisible, notif) => {
    if (isVisible) {
      setTimer(setTimeout(() => {
        onVisibilityChange(notif)
      }, 3000))
    } else {
      clearTimeout(timer)
    }
  }, [timer])
  return (
    <VisibilitySensor
      active={notification.viewed === NotificationViewed.UNREAD}
      offset={{ top: -10, bottom: -10 }}
      containment={containment.current}
      onChange={(isVisible) => setVisible(isVisible, notification)}
    >
      <div data-cy="notif-block" className={blockClassNames.join(' ')} onTransitionEnd={(event) => onTransitionEnd(event, notification)}>
        <div className={styles.content}>
          <span className={styles.header}>{notification.title}</span>
          <span className={styles.message}>{SimpleMarkdown.markdownToReact(notification.message)}</span>
          {(notification.callToActionCallback && notification.callToActionText) ? (
            <a href={notification.callToActionCallback} className={styles.cta}>{notification.callToActionText}</a>
          ) : null}

        </div>
        <div className={styles.side}>
          <span
            className={styles.side_action}
          >
              <Popover
                data-cy="notif-side-action"
                content={<>
                    <Button type="link" size="small" data-cy="notif-side-action-content" onClick={(event) => onArchiveClicked(event)}>{(notification.status === NotificationStatus.ARCHIVED) ? 'Re-open' : 'Dismiss'}</Button>
                </>}
                placement="left"
                trigger="click"
                visible={actionOpen}
                onVisibleChange={(visible) => setActionOpen(visible)}
              >
                <FontAwesomeIcon style={{ width: '20px', height: '20px' }} icon={faEllipsisV} />
              </Popover>
          </span>
          <Popover
            content={<>
              <span className={styles.popper_date}>{lastAccessDateFormat(notification.createdDate)}</span>
            </>}
            placement="left"
            trigger="hover"
          >
            <span
              className={styles.side_date}
            >
                {renderDateDiff(notification.createdDate)}
            </span>
          </Popover>
        </div>
      </div>
    </VisibilitySensor>
  )
}

export default NotificationBlock
